import React, { useState, useEffect } from 'react';
import { Calendar, Plus, Trash2, Save, X } from 'lucide-react';
import axiosInstance from '../networking/axiosconfig';
import axios from 'axios';
import Navbar from './Navbar';

const LOCAL_API_BASE_URL = 'http://attendance.inurum.com/api';

const HolidayManagement = () => {
    const [holidays, setHolidays] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);

    const [formData, setFormData] = useState({
        holiday_name: '',
        start_date: '',
        end_date: '',
    });
    const [saving, setSaving] = useState(false);
    const [userRole, setUserRole] = useState(null);

    const holidayOptions = [
        "Republic Day",
        "Holi",
        "Raksha Bandhan",
        "Independence Day",
        "Ram Navmi",
        "Gandhi Jayanti",
    ];


    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'];

    useEffect(() => {
        // Get user role from localStorage
        const role = localStorage.getItem("role");
        setUserRole(role ? Number(role) : null);
        fetchHolidays();
    }, []);

    const fetchHolidays = async () => {
        try {
            setLoading(true);
            setError(null);
            const token = localStorage.getItem("token");
            const response = await axios.get(`${LOCAL_API_BASE_URL}/get/holiday`, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });
            const data = response?.data?.data || [];
            setHolidays(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('Error fetching holidays:', err);
            setError(err.response?.data?.message || 'Failed to fetch holidays');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenModal = () => {
        setFormData({
            holiday_name: '',
            start_date: '',
            end_date: '',
        });
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setFormData({
            holiday_name: '',
            start_date: '',
            end_date: '',
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.holiday_name.trim() || !formData.start_date) {
            setError('Holiday name and start date are required');
            return;
        }


        try {
            setSaving(true);
            setError(null);
            const token = localStorage.getItem("token");

            // Create new holiday
            const response = await axiosInstance.post(`${LOCAL_API_BASE_URL}/holiday`, {
                holiday_name: formData.holiday_name.trim(),
                start_date: formData.start_date,
                end_date: formData.end_date
            }, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            if (response.data) {
                await fetchHolidays();
                handleCloseModal();
            }
        } catch (err) {
            console.error('Error saving holiday:', err);
            setError(err.response?.data?.message || 'Failed to save holiday');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (holidayId) => {
        if (!window.confirm('Are you sure you want to delete this holiday?')) {
            return;
        }

        try {
            setError(null);
            const token = localStorage.getItem("token");
            const response = await axiosInstance.delete(`${LOCAL_API_BASE_URL}/holiday/${holidayId}`, {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                }
            });

            // Check if deletion was successful
            if (response.status === 200 || response.status === 204) {
                await fetchHolidays();
            }
        } catch (err) {
            console.error('Error deleting holiday:', err);
            setError(err.response?.data?.message || 'Failed to delete holiday');
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return dateString;
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            weekday: 'long'
        });
    };

    const getMonthYear = (dateString) => {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '';
        return `${monthNames[date.getMonth()]} ${date.getFullYear()}`;
    };

    // Sort holidays by date
    const sortedHolidays = [...holidays].sort((a, b) => {
        const dateA = new Date(a.date || a.start_date);
        const dateB = new Date(b.date || b.start_date);
        return dateA - dateB;
    });

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
            <Navbar />
            <div className="max-w-7xl mx-auto p-6">
                {/* Header */}
                <div className="bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-800 rounded-2xl shadow-2xl p-8 mb-6 text-white">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <div className="bg-white bg-opacity-20 p-3 rounded-lg">
                                <Calendar size={32} />
                            </div>
                            <div>
                                <h1 className="text-3xl md:text-4xl font-bold mb-2">Holiday Management</h1>
                                <p className="text-blue-100">
                                    {userRole === 3 ? 'View public holidays and special days' : 'Manage public holidays and special days'}
                                </p>
                            </div>
                        </div>
                        {/* Only show Add Holiday button for Owner/Pivot (1) and Manager (2) */}
                        {(userRole === 1 || userRole === 2) && (
                            <button
                                onClick={() => handleOpenModal()}
                                className="flex items-center gap-2 px-6 py-3 bg-white text-blue-600 rounded-lg hover:bg-blue-50 transition-colors font-semibold shadow-lg"
                            >
                                <Plus size={20} />
                                <span>Add Holiday</span>
                            </button>
                        )}
                    </div>
                </div>

                {/* Error Message */}
                {error && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 text-red-700">
                        {error}
                    </div>
                )}

                {/* Holidays List */}
                {loading ? (
                    <div className="bg-white rounded-xl shadow-lg p-12 text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                        <p className="text-gray-600">Loading holidays...</p>
                    </div>
                ) : (
                    <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                        <div className="p-6 border-b border-gray-200">
                            <h2 className="text-xl font-bold text-gray-800">All Holidays</h2>
                            <p className="text-sm text-gray-500 mt-1">Total: {sortedHolidays.length} holidays</p>
                        </div>

                        {sortedHolidays.length === 0 ? (
                            <div className="p-12 text-center text-gray-500">
                                <Calendar size={48} className="mx-auto mb-4 text-gray-300" />
                                <p className="text-lg">No holidays found</p>
                                {userRole !== 3 && (
                                    <p className="text-sm mt-2">Click "Add Holiday" to create your first holiday</p>
                                )}
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Date</th>
                                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Holiday Name</th>
                                            <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Month/Year</th>
                                            {/* Only show Actions column for Owner/Pivot (1) and Manager (2) */}
                                            {(userRole === 1 || userRole === 2) && (
                                                <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                                            )}
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {sortedHolidays.map((holiday) => {
                                            // Try multiple possible ID fields (UUID format or numeric)
                                            const holidayId = holiday.holiday_id_PK ||
                                                holiday.holiday_id ||
                                                holiday.id ||
                                                holiday.uuid ||
                                                holiday._id;
                                            return (
                                                <tr key={holidayId} className="hover:bg-gray-50 transition-colors">
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="text-sm font-medium text-gray-900">
                                                            {formatDate(holiday.date || holiday.start_date)}
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <div className="text-sm text-gray-900 font-semibold">
                                                            {holiday.holiday_name || holiday.name || 'Unnamed Holiday'}
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap">
                                                        <div className="text-sm text-gray-500">
                                                            {getMonthYear(holiday.date || holiday.start_date)}
                                                        </div>
                                                    </td>
                                                    {/* Only show Actions column for Owner/Pivot (1) and Manager (2) */}
                                                    {(userRole === 1 || userRole === 2) && (
                                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                            <div className="flex items-center justify-end gap-2">
                                                                <button
                                                                    onClick={() => handleDelete(holidayId)}
                                                                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                                    title="Delete"
                                                                >
                                                                    <Trash2 size={18} />
                                                                </button>
                                                            </div>
                                                        </td>
                                                    )}
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}

                {/* Add/Edit Modal */}
                {showModal && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
                            <div className="bg-gradient-to-r from-blue-600 to-indigo-800 text-white p-6 flex justify-between items-center rounded-t-2xl">
                                <h2 className="text-2xl font-bold">
                                    Add New Holiday
                                </h2>
                                <button
                                    onClick={handleCloseModal}
                                    className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-lg transition-colors"
                                >
                                    <X size={24} />
                                </button>
                            </div>

                            <form onSubmit={handleSubmit} className="p-6">
                                {error && (
                                    <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
                                        {error}
                                    </div>
                                )}
                                <div className="mb-4 relative">
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        Holiday Name <span className="text-red-500">*</span>
                                    </label>

                                    <input
                                        type="text"
                                        value={formData.holiday_name}
                                        onChange={(e) => {
                                            setFormData({ ...formData, holiday_name: e.target.value });
                                            setShowDropdown(true);
                                        }}
                                        onFocus={() => setShowDropdown(true)}
                                        onBlur={() => setTimeout(() => setShowDropdown(false), 150)}
                                        placeholder="Select or type holiday name"
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                        required
                                    />

                                    {showDropdown && (
                                        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                                            {holidayOptions
                                                .filter((holiday) =>
                                                    holiday.toLowerCase().includes(formData.holiday_name.toLowerCase())
                                                )
                                                .map((holiday, index) => (
                                                    <div
                                                        key={index}
                                                        onMouseDown={() => {
                                                            setFormData({ ...formData, holiday_name: holiday });
                                                            setShowDropdown(false);
                                                        }}
                                                        className="px-4 py-2 cursor-pointer hover:bg-blue-50 text-gray-700"
                                                    >
                                                        {holiday}
                                                    </div>
                                                ))}

                                            {holidayOptions.filter((h) =>
                                                h.toLowerCase().includes(formData.holiday_name.toLowerCase())
                                            ).length === 0 && (
                                                    <div className="px-4 py-2 text-gray-400 text-sm">
                                                        Press Enter to add custom holiday
                                                    </div>
                                                )}
                                        </div>
                                    )}
                                </div>


                                <div className="mb-6">
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        Start Date <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="date"
                                        value={formData.start_date}
                                        onChange={(e) =>
                                            setFormData({ ...formData, start_date: e.target.value })
                                        }
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                        required
                                    />

                                </div>

                                <div className="mb-6">
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                        End Date
                                    </label>
                                    <input
                                        type="date"
                                        value={formData.end_date}
                                        onChange={(e) =>
                                            setFormData({ ...formData, end_date: e.target.value })
                                        }
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    />

                                </div>

                                <div className="flex justify-end gap-3">
                                    <button
                                        type="button"
                                        onClick={handleCloseModal}
                                        className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                                        disabled={saving}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={saving}
                                        className={`px-6 py-3 rounded-lg flex items-center gap-2 font-medium transition-colors ${saving
                                            ? 'bg-blue-300 text-white cursor-not-allowed'
                                            : 'bg-blue-600 text-white hover:bg-blue-700'
                                            }`}
                                    >
                                        <Save size={18} />
                                        {saving ? 'Saving...' : 'Add Holiday'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default HolidayManagement;

